import { useState, useEffect, useCallback } from 'react'
import {
  CustomersService,
  ChangeLog,
  ChangeLogRequest,
} from '@/api/generated-client'
import { useToast } from './useToast'
import { t } from '@/i18n'

interface UseChangeLogOptions {
  customerId?: number
}

interface UseChangeLogReturn {
  changeLogs: ChangeLog[]
  loading: boolean
  submitting: boolean
  addChangeLog: (data: ChangeLogRequest) => Promise<void>
  refreshChangeLogs: () => Promise<void>
}

export const useChangeLog = ({
  customerId,
}: UseChangeLogOptions): UseChangeLogReturn => {
  const [changeLogs, setChangeLogs] = useState<ChangeLog[]>([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const addToast = useToast()

  const fetchChangeLogs = useCallback(async () => {
    if (!customerId) return

    setLoading(true)
    try {
      const response = await CustomersService.customersCustomersLogsList({
        customerId,
      })
      setChangeLogs(response)
    } catch (err) {
      console.error('Error fetching change logs:', err)
      addToast('error', t('error_fetching_change_logs'))
    } finally {
      setLoading(false)
    }
  }, [customerId, addToast])

  const addChangeLog = useCallback(
    async (data: ChangeLogRequest) => {
      if (!customerId) return

      setSubmitting(true)
      try {
        const newChangeLog =
          await CustomersService.customersCustomersLogsCreate({
            customerId,
            requestBody: data,
          })
        setChangeLogs(prev => [newChangeLog, ...prev])
        addToast('success', t('change_log_added_successfully'))
      } catch (err) {
        console.error('Error adding change log:', err)
        addToast('error', t('error_adding_change_log'))
      } finally {
        setSubmitting(false)
      }
    },
    [customerId, addToast],
  )

  const refreshChangeLogs = useCallback(async () => {
    await fetchChangeLogs()
  }, [fetchChangeLogs])

  useEffect(() => {
    if (customerId) {
      fetchChangeLogs()
    }
  }, [customerId, fetchChangeLogs])

  return {
    changeLogs,
    loading,
    submitting,
    addChangeLog,
    refreshChangeLogs,
  }
}
